import { Link, useParams } from "react-router-dom";
import { typeLabels } from "@/mocks/catalog";
import { getMovieById, getRelatedMovies } from "@/utils/data";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import FavoriteButton from "@/components/feature/FavoriteButton";
import ShareButton from "@/components/feature/ShareButton";

export default function MovieDetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);

  usePageMeta({
    title: movie ? `${movie.title} - 黄色在线` : "影视详情 - 黄色在线",
    description: movie
      ? `${movie.title}（${movie.year}）剧情简介与主演介绍：${movie.synopsis.slice(0, 60)}`
      : "影视作品详情页。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},${movie.actors.join(",")},黄色在线`
      : "黄色在线,影视详情",
  });

  if (!movie) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-10 text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true" />
          <h1 className="font-heading text-xl font-bold text-foreground-950">未找到该影视作品</h1>
          <p className="text-sm text-foreground-500">作品可能已下线，去看看其它内容吧。</p>
          <Link
            to="/category/movie"
            className="mt-2 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            <i className="ri-compass-3-line" aria-hidden="true" />
            浏览电影分类
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 5);
  const categoryTo = `/category/${movie.type}`;

  const infoRows = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.actors.join(" / ") },
    { label: "类型", value: movie.genres.join(" / ") },
    { label: "上映年份", value: String(movie.year) },
    { label: "地区", value: movie.region },
    { label: "片长 / 集数", value: movie.duration },
  ];

  return (
    <div className="w-full">
      {/* Banner */}
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} - 黄色在线`}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/50" />
        <div className="relative w-full px-4 py-8 md:px-8 md:py-12">
          <Breadcrumb
            light
            items={[{ label: typeLabels[movie.type], to: categoryTo }, { label: movie.title }]}
          />
          <div className="mt-6 flex flex-col gap-6 sm:flex-row sm:items-end">
            <div className="w-36 flex-shrink-0 overflow-hidden rounded-lg border border-background-50/15 sm:w-44 md:w-52">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - 黄色在线`}
                className="h-full w-full object-cover object-top"
                style={{ aspectRatio: "2 / 3" }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-white">
                  {typeLabels[movie.type]}
                </span>
                <span className="rounded-md bg-background-50/15 px-2 py-0.5 text-xs font-medium text-background-100">
                  {movie.status}
                </span>
              </div>
              <h1 className="mt-3 font-heading text-2xl font-extrabold text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-background-300">
                <span className="inline-flex items-center gap-1 font-semibold text-accent-300">
                  <i className="ri-star-fill text-xs" aria-hidden="true" />
                  {movie.rating.toFixed(1)}
                </span>
                <span>{movie.year}</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <span
                    key={g}
                    className="rounded-md border border-background-50/20 px-2.5 py-1 text-xs text-background-200"
                  >
                    {g}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <FavoriteButton movieId={movie.id} variant="ghost" />
                <ShareButton variant="ghost" />
                <Link
                  to={categoryTo}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2 text-sm font-medium text-background-100 transition-colors hover:border-accent-400 hover:text-accent-300"
                >
                  <i className="ri-apps-2-line" aria-hidden="true" />
                  更多{typeLabels[movie.type]}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {/* Playback placeholder */}
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-play-circle-line text-3xl" aria-hidden="true" />
              </span>
              <p className="font-heading text-base font-bold text-foreground-800">暂无播放资源</p>
              <p className="max-w-sm text-sm text-foreground-500">
                本页面为影视资源展示示例，不提供影片播放与下载服务。
              </p>
            </div>

            <div className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
                {movie.synopsis}
              </p>
            </div>

            <div className="mt-8">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                基本信息
              </h2>
              <dl className="mt-3 overflow-hidden rounded-lg border border-background-200">
                {infoRows.map((row, i) => (
                  <div
                    key={row.label}
                    className={`flex flex-col gap-1 px-4 py-3 sm:flex-row sm:gap-4 ${
                      i % 2 === 0 ? "bg-background-50" : "bg-background-100"
                    }`}
                  >
                    <dt className="w-24 flex-shrink-0 text-sm text-foreground-500">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Sidebar recommendations */}
          <aside className="lg:col-span-4">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
              相关推荐
            </h2>
            <div className="mt-4 flex flex-col gap-3">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/movie/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-2.5 transition-colors hover:border-accent-300 hover:bg-accent-50/50"
                >
                  <div className="h-20 w-14 flex-shrink-0 overflow-hidden rounded-md bg-secondary-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 海报`}
                      title={`${item.title} - 黄色在线`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-sm font-bold text-foreground-950 transition-colors group-hover:text-accent-600">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500">
                      {item.year} · {item.region}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs text-foreground-400">
                      {item.genres.join(" / ")}
                    </p>
                    <span className="mt-1 inline-flex items-center gap-0.5 text-xs font-semibold text-accent-600">
                      <i className="ri-star-fill text-[10px]" aria-hidden="true" />
                      {item.rating.toFixed(1)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>

        {/* Full-width recommendations */}
        <div className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
            你可能还喜欢
          </h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {getRelatedMovies(movie, 5).map((item) => (
              <MovieCard key={`grid-${item.id}`} movie={item} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}